// src/cockpit4/components/BrandMonitorPlansPanel.tsx
// ============================================
// 品牌监测计划（对应图2）
//
// 顶栏：监测计划 标题 ｜ 右侧：「正在监测中: 0个计划 ｜ 0个AI问题 ｜ 预计每天消耗 0.00 积分」｜ + 增加监测计划
// 表格头：监测品牌 ▼ / 计划名称 ▼ / 创建 / AI问题 / 监测平台 / 监测频次 / 今日任务进度 / 预计消耗 / 创建时间 ▼ / 状态 ▼ / 操作
// 空态：紫蓝开箱纸飞机 ｜ 「暂无监测中的品牌」
// ============================================

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus, ArrowUpDown, Pause, Play, Settings, Trash2, Edit3, Eye, Clock, Coins, ListChecks, Users,
} from "lucide-react";
import EmptyState from "./_MonitorEmptyState";

interface Plan {
  id: string;
  brand: string;
  name: string;
  creator: string;
  qCount: number;
  platforms: string[];
  freq: "每小时" | "每天" | "每周";
  progress: number; // 0-100
  credit: number;
  createdAt: string;
  status: "运行中" | "已暂停" | "已结束";
}

const FREQ_TEXT: Record<string, "每小时" | "每天" | "每周"> = {
  hourly: "每小时", daily: "每天", weekly: "每周",
};

// 后端品牌监测计划（/ai-visibility/brands 真实配置，无模拟数据） → 面板行
function mapPlan(b: any): Plan {
  const brand = b.brandName || "未命名品牌";
  const qCount = Array.isArray(b.questions) ? b.questions.length : 0;
  const platforms = Array.isArray(b.platforms) ? b.platforms : [];
  return {
    id: b.id || "",
    brand,
    name: `${brand} 监测计划`,
    creator: "XEAOS 系统",
    qCount,
    platforms,
    freq: FREQ_TEXT[b.frequency] || "每天",
    progress: 0,
    credit: 0,
    createdAt: b.createdAt || "",
    status: b.enabled === false ? "已暂停" : "运行中",
  };
}

export default function BrandMonitorPlansPanel({
  onAddBrand,
}:{ onAddBrand?: () => void }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const runCount = plans.filter(p => p.status === "运行中").length;
  const totalQ = plans.reduce((s,p)=>s+p.qCount, 0);
  const totalCredit = plans.reduce((s,p)=>s+p.credit, 0);

  // 读取后端真实监测计划（Obsidian Vault 持久化）
  useEffect(() => {
    fetch("/ai-visibility/brands")
      .then((r) => r.json())
      .then((d) => {
        if (d?.success && Array.isArray(d.brands)) {
          setPlans(d.brands.map(mapPlan));
        }
      })
      .catch(() => { /* 后端不可达时保持空态 */ });
  }, []);

  const toggleStatus = (id:string) => setPlans(plans.map(p => p.id===id ? {
    ...p, status: (p.status==="运行中" ? "已暂停" : "运行中"),
  } : p));

  return (
    <div className="px-4 py-4">
      {/* 顶部条 */}
      <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3 border-b border-white/[0.06]">
        <div className="text-[15px] font-bold text-white flex items-center gap-2">
          <Clock size={16} className="text-violet-400" />
          监测计划
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1 text-[13px] text-gray-400">
            正在监测中：<span className="text-violet-300 font-semibold mx-0.5">{runCount}</span> 个计划
          </div>
          <div className="flex items-center gap-1 text-[13px] text-gray-400">
            <span className="text-violet-300 font-semibold mx-0.5">{totalQ}</span> 个AI问题
          </div>
          <div className="flex items-center gap-1 text-[13px] text-gray-400">
            预计每天消耗 <Coins size={13} className="text-amber-400 mx-0.5" />
            <span className="text-violet-300 font-semibold mx-0.5">{totalCredit.toFixed(2)}</span> 积分
          </div>
          <button onClick={onAddBrand}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/20 hover:opacity-95 transition-all">
            <Plus size={14} /> 增加监测计划
          </button>
        </div>
      </div>

      {/* 表格区 */}
      <div className="mt-3 rounded-2xl bg-white/[0.03] border border-white/[0.08] overflow-hidden">
        {plans.length === 0 ? (
          <EmptyState text="暂无监测中的品牌" />
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-white/[0.04] text-gray-400 text-[11.5px]">
                  <th className="text-left p-3 rounded-l-lg"><span className="inline-flex items-center gap-1">监测品牌 <ArrowUpDown size={10} /></span></th>
                  <th className="text-left p-3"><span className="inline-flex items-center gap-1">计划名称 <ArrowUpDown size={10} /></span></th>
                  <th className="text-left p-3">创建</th>
                  <th className="text-left p-3">AI问题</th>
                  <th className="text-left p-3">监测平台</th>
                  <th className="text-left p-3">监测频次</th>
                  <th className="text-left p-3">今日任务进度</th>
                  <th className="text-left p-3">预计消耗</th>
                  <th className="text-left p-3"><span className="inline-flex items-center gap-1">创建时间 <ArrowUpDown size={10} /></span></th>
                  <th className="text-left p-3"><span className="inline-flex items-center gap-1">状态 <ArrowUpDown size={10} /></span></th>
                  <th className="text-left p-3 rounded-r-lg">操作</th>
                </tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border border-violet-400/30 flex items-center justify-center text-[11px] text-violet-200 font-bold">
                          {p.brand.slice(0,1)}
                        </div>
                        <span className="text-white font-medium">{p.brand}</span>
                      </div>
                    </td>
                    <td className="p-3 text-gray-200">{p.name}</td>
                    <td className="p-3 text-gray-400">{p.creator}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-violet-500/10 border border-violet-400/30 text-violet-200">
                      <ListChecks size={10} /> {p.qCount}
                    </span></td>
                    <td className="p-3 text-gray-400 text-[11.5px] max-w-[160px] truncate" title={p.platforms.join("、")}>
                      {p.platforms.slice(0,3).join("、")}{p.platforms.length>3 ? ` +${p.platforms.length-3}`:""}
                    </td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[11px] bg-cyan-500/10 border border-cyan-400/30 text-cyan-300">{p.freq}</span></td>
                    <td className="p-3 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-violet-500 to-cyan-500" style={{ width: `${p.progress}%` }} />
                        </div>
                        <span className="text-gray-300 font-mono text-[11px]">{p.progress}%</span>
                      </div>
                    </td>
                    <td className="p-3 text-gray-300 font-mono text-[12px]">{p.credit.toFixed(2)}</td>
                    <td className="p-3 text-gray-400">{p.createdAt}</td>
                    <td className="p-3">
                      {p.status==="运行中"
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/10 border border-emerald-400/30 text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 运行中</span>
                        : p.status==="已暂停"
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-amber-500/10 border border-amber-400/30 text-amber-300"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> 已暂停</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-gray-500/10 border border-gray-400/30 text-gray-400">已结束</span>
                      }
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button onClick={()=>toggleStatus(p.id)} title={p.status==="运行中"?"暂停":"启用"}
                          className={`w-7 h-7 rounded-md flex items-center justify-center border transition-all ${
                            p.status==="运行中" ? "bg-amber-500/10 border border-amber-400/30 text-amber-300 hover:bg-amber-500/20" : "bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/20"
                          }`}>
                          {p.status==="运行中" ? <Pause size={11} /> : <Play size={11} />}
                        </button>
                        <IconBtn Icon={<Eye size={12} />} />
                        <IconBtn Icon={<Edit3 size={12} />} />
                        <IconBtn Icon={<Settings size={12} />} />
                        <IconBtn Icon={<Trash2 size={12} />} danger />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* 避免 eslint 未使用变量 */}
            <div className="hidden"><Users /></div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function IconBtn({ Icon, danger }:{ Icon: React.ReactNode; danger?: boolean }) {
  return (
    <button className={`w-7 h-7 rounded-md flex items-center justify-center border transition-all ${
      danger ? "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-rose-300 hover:border-rose-400/40"
             : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-violet-300 hover:border-violet-400/40"
    }`}>{Icon}</button>
  );
}
